import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { PageHero } from "@/components/sections/PageHero";
import { COMPANY, SERVICES, IMAGES } from "@/lib/site";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, Phone, Mail, MapPin, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  company: "", contact_name: "", email: "", phone: "",
  industry: "", service: "", part_quantity: "", part_dimensions: "",
  substrate: "", color: "", timeline: "", message: "",
};

export default function Quote() {
  const [form, setForm] = useState(initial);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact_name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success("Quote request received.");
      nav("/thank-you");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="quote-page">
      <PageHero
        eyebrow="Request a Quote"
        title={<>Send your specs. <span className="text-[#FF5722]">We respond in 24 hours.</span></>}
        subtitle={`Drawings, photos, and part lists welcome. Or call ${COMPANY.phone} to speak to us directly.`}
        image={IMAGES.powder1}
      />

      <section className="section px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <form onSubmit={submit} className="lg:col-span-8 space-y-8" data-testid="quote-form">
          <div className="flex items-center gap-4 mb-4">
            {[1, 2, 3].map(n => (
              <div key={n} className="flex items-center gap-2">
                <div className={`w-8 h-8 flex items-center justify-center border ${step >= n ? "bg-[#FF5722] border-[#FF5722] text-white" : "border-white/20 text-zinc-500"} font-display`}>{n}</div>
                {n < 3 && <div className={`w-12 h-px ${step > n ? "bg-[#FF5722]" : "bg-white/10"}`} />}
              </div>
            ))}
            <span className="ml-4 eyebrow">Step {step} of 3</span>
          </div>

          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="quote-step-1">
              <Field label="Company *"><Input value={form.company} onChange={e => upd("company", e.target.value)} data-testid="quote-company" required /></Field>
              <Field label="Contact Name *"><Input value={form.contact_name} onChange={e => upd("contact_name", e.target.value)} data-testid="quote-contact-name" required /></Field>
              <Field label="Email *"><Input type="email" value={form.email} onChange={e => upd("email", e.target.value)} data-testid="quote-email" required /></Field>
              <Field label="Phone *"><Input type="tel" value={form.phone} onChange={e => upd("phone", e.target.value)} data-testid="quote-phone" required /></Field>
              <Field label="Industry">
                <Select value={form.industry} onValueChange={v => upd("industry", v)}>
                  <SelectTrigger className="bg-transparent border-white/20 rounded-none h-11" data-testid="quote-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent>
                    {["General Contractor", "Fabricator", "OEM Manufacturer", "Architect / Engineer", "Government Contractor", "Automotive / Restoration", "Other"].map(x => (
                      <SelectItem key={x} value={x}>{x}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Service Needed *">
                <Select value={form.service} onValueChange={v => upd("service", v)} required>
                  <SelectTrigger className="bg-transparent border-white/20 rounded-none h-11" data-testid="quote-service"><SelectValue placeholder="Select service" /></SelectTrigger>
                  <SelectContent>
                    {SERVICES.map(s => <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="md:col-span-2 flex justify-end">
                <button type="button" onClick={() => setStep(2)} className="btn-primary" data-testid="quote-next-1">Next Step <ArrowRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="quote-step-2">
              <Field label="Part Quantity"><Input value={form.part_quantity} onChange={e => upd("part_quantity", e.target.value)} placeholder="e.g., 40 units / weekly" data-testid="quote-quantity" /></Field>
              <Field label="Part Dimensions"><Input value={form.part_dimensions} onChange={e => upd("part_dimensions", e.target.value)} placeholder="e.g., 12 ft x 4 ft x 3 in" data-testid="quote-dimensions" /></Field>
              <Field label="Substrate"><Input value={form.substrate} onChange={e => upd("substrate", e.target.value)} placeholder="e.g., HR steel, aluminum, cast" data-testid="quote-substrate" /></Field>
              <Field label="Color / Finish"><Input value={form.color} onChange={e => upd("color", e.target.value)} placeholder="RAL, chip, custom, etc." data-testid="quote-color" /></Field>
              <Field label="Timeline">
                <Select value={form.timeline} onValueChange={v => upd("timeline", v)}>
                  <SelectTrigger className="bg-transparent border-white/20 rounded-none h-11" data-testid="quote-timeline"><SelectValue placeholder="Select timeline" /></SelectTrigger>
                  <SelectContent>
                    {["ASAP / Rush", "1-2 weeks", "3-4 weeks", "Recurring / Program", "Flexible"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="md:col-span-2 flex justify-between">
                <button type="button" onClick={() => setStep(1)} className="btn-ghost" data-testid="quote-back-2">← Back</button>
                <button type="button" onClick={() => setStep(3)} className="btn-primary" data-testid="quote-next-2">Next Step <ArrowRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" data-testid="quote-step-3">
              <Field label="Project Details / Notes">
                <Textarea rows={8} value={form.message} onChange={e => upd("message", e.target.value)} placeholder="Describe the project, prep needs, spec, delivery, and any drawings you can share." data-testid="quote-message" />
              </Field>
              <div className="flex justify-between">
                <button type="button" onClick={() => setStep(2)} className="btn-ghost">← Back</button>
                <button type="submit" disabled={submitting} className="btn-primary" data-testid="quote-submit">
                  {submitting ? "Submitting..." : "Submit Request"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </form>

        <aside className="lg:col-span-4">
          <div className="border border-white/10 p-8 bg-[#0B0B0D] sticky top-32 space-y-6">
            <div>
              <div className="eyebrow mb-3">Prefer to Talk?</div>
              <div className="font-display text-3xl tracking-tight leading-tight">Call us directly.</div>
              <p className="text-zinc-400 text-sm mt-3">Our team answers estimating calls during business hours.</p>
            </div>
            <div className="space-y-3 text-sm text-zinc-200">
              <div className="flex gap-3"><Phone className="w-4 h-4 text-[#FF5722] shrink-0 mt-0.5" /><a href={COMPANY.phoneHref}>{COMPANY.phone}</a></div>
              <div className="flex gap-3"><Mail className="w-4 h-4 text-[#FF5722] shrink-0 mt-0.5" /><a href={COMPANY.emailHref} className="break-all">{COMPANY.email}</a></div>
              <div className="flex gap-3"><MapPin className="w-4 h-4 text-[#FF5722] shrink-0 mt-0.5" />{COMPANY.city}</div>
              <div className="flex gap-3"><Clock className="w-4 h-4 text-[#FF5722] shrink-0 mt-0.5" />{COMPANY.hours}</div>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-zinc-400 mb-2 block">{label}</Label>
    {children}
  </div>
);
